// 查资料之前先看问题里有没有说「在哪儿找」：我写的、某个分区。
// 说了就只在那几篇里找（只带 knowledge_ids、不带库，灵智原生才只搜这几篇）；没说就照原来的范围。
// 全是规则，不调模型：提问不多等。2026-10-12 的调研：先按来源、日期过滤再检索，比只靠内容像不像准得多（见 lingzhi-server docs/lingzhi/retrieval-research）。

/** 知识库里的一个顶层文件夹（分区）。 */
export interface FolderRef {
  kbId: string
  path: string
}

/** 要缩到的分区；mine：是「我写的」。 */
export interface Need {
  kbId: string
  folder: string
  mine: boolean
}

export const MINE_FOLDER = '我写的'

// ———— 来源 ————

const NOT_ME = '(?<![帮给替为让叫请和跟同])'
const MINE = [
  // 我写过、我写的、我之前发过的（「我写的代码」不算）
  new RegExp(`${NOT_ME}我(?:自己|之前|以前|曾经|过去|都|去年|今年)?(?:写|发|发表)(?:过|的)(?!\\s*(?:代码|程序|脚本|函数|bug|sql|正则|接口|组件|测试|单测))`, 'i'),
  // 我的文章、我的随想
  new RegExp(`${NOT_ME}我的(?:文章|随想|稿子|文字|作品|博客|公众号|帖子|简历)`),
  // 我在文章里写过、我的文章里提到
  new RegExp(`${NOT_ME}我(?:在|的)(?:文章|稿子|随想|公众号)里?(?:写|说|提|讲)(?:过|到)?`),
]
export const isAboutMine = (q: string) => MINE.some((r) => r.test(q))

/** 问自己写过的、又要和别处对照的：不缩到「我写的」。 */
const MIXED = /收集|别人|他人|其他人|其他资料|外面|业界|大家|对比|相比|比较一下|分别/

/** 去掉空白、统一大小写和全角，用来比分区名。 */
const norm = (s: string) => s.normalize('NFKC').toLowerCase().replace(/\s+/g, '')
/** 名字太常见、单独出现多半不是在说分区的：要加引号或「分区、文件夹」才算。 */
const AMBIGUOUS = new Set(['思考'])

/** 问题里点名的分区：「项目资料里…」「在素材中…」「「思考」分区」；四个字以上的名字（项目资料、英文日报）出现就算。点了两个以上就不算（不知道该缩到哪个）。 */
export function findFolder(q: string, folders: FolderRef[]): FolderRef | null {
  const hits = findFolders(q, folders)
  return hits.length === 1 ? hits[0] : null
}

function findFolders(q: string, folders: FolderRef[]): FolderRef[] {
  const nq = norm(q)
  const named = [...folders].sort((a, b) => b.path.length - a.path.length)
  const hits: FolderRef[] = []
  for (const f of named) {
    const name = norm(f.path)
    if (name.length < 2) continue
    for (let i = nq.indexOf(name); i >= 0; i = nq.indexOf(name, i + 1)) {
      const before = nq.slice(Math.max(0, i - 1), i)
      const after = nq.slice(i + name.length, i + name.length + 5)
      const quoted = /[「『“"《]/.test(before) && /^[」』”"》]/.test(after)
      const section = /^[」』”"》]?(?:这个|那个)?(?:分区|文件夹|目录)/.test(after)
      const inside = /^[」』”"》]?(?:里|中|下面|下的|这个区|那个区)/.test(after) || /[在从去到]$/.test(before)
      if (quoted || section || name.length >= 4 || (inside && !AMBIGUOUS.has(f.path))) {
        if (!hits.some((h) => h.path === f.path)) hits.push(f)
        break
      }
    }
  }
  return hits
}

/**
 * 这个问题要不要缩小范围、缩到哪儿。点名的分区优先，其次「我写的」。
 * folders 是这次要查的那几个库的顶层文件夹。
 */
export function parseNeed(q: string, folders: FolderRef[]): Need | null {
  // 拿不准就不缩：点了两个分区、或者「我写的和收集的」「对比别人怎么说」这种要两边一起看的
  if (findFolders(q, folders).length > 1) return null
  const named = findFolder(q, folders)
  if (named) return { kbId: named.kbId, folder: named.path, mine: named.path === MINE_FOLDER }
  if (isAboutMine(q)) {
    if (MIXED.test(q)) return null
    const mine = folders.find((f) => f.path === MINE_FOLDER)
    if (mine) return { kbId: mine.kbId, folder: MINE_FOLDER, mine: true }
  }
  return null
}

/** 解析出来以后，真正去查的范围。label 显示在回答的过程里。 */
export interface Routed {
  label: string
  knowledgeBaseIds: string[]
  knowledgeIds: string[]
}

export function routeFolder(need: Need, docs: { id: string }[]): Routed | null {
  if (!docs.length) return null
  return { label: `只在「${need.folder}」里找（${docs.length} 篇）`, knowledgeBaseIds: [], knowledgeIds: docs.map((d) => d.id) }
}
